import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, ReferenceLine, PieChart, Pie, Cell } from "recharts";
import { usd, usdCompact } from "@/lib/format";
import { Card } from "@/components/common/Ornament";

export const PALETTE = ["#0F2E23", "#C59B27", "#3E5C4E", "#8AA399", "#D4AF37", "#B7A88A"];
const axis = { fontSize: 11, fill: "#64748B" };
const tip = { contentStyle: { borderRadius: 6, border: "1px solid #E2DCD2", fontSize: 12 }, formatter: (v) => usd(v) };

export function RevenueNoiChart({ years }) {
  const data = years.map((y) => ({ name: `Y${y.year}`, Revenue: y.totalRev, NOI: y.noi }));
  return (
    <Card eyebrow="By Year" title="Revenue & NOI" testId="financial-chart-revenue">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 0, right: 8 }}>
            <CartesianGrid stroke="#E2DCD2" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={axis} />
            <YAxis tick={axis} tickFormatter={usdCompact} width={60} />
            <Tooltip {...tip} cursor={{ fill: "rgba(197,155,39,.08)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Revenue" fill="#0F2E23" radius={[3, 3, 0, 0]} />
            <Bar dataKey="NOI" fill="#C59B27" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function CumulativeChart({ data }) {
  return (
    <Card eyebrow="Levered" title="Cumulative Cash Flow" testId="financial-chart-cashflow">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: 0, right: 8 }}>
            <CartesianGrid stroke="#E2DCD2" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="year" tick={axis} />
            <YAxis tick={axis} tickFormatter={usdCompact} width={60} />
            <Tooltip {...tip} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Line type="monotone" dataKey="cumulative" name="Cumulative" stroke="#0F2E23" strokeWidth={2.5} dot={{ r: 3, fill: "#C59B27", stroke: "#C59B27" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-slate-500 mt-2">Starts at −equity in Year 0; final year includes net sale proceeds less loan payoff.</p>
    </Card>
  );
}

export function CostDonut({ data, total }) {
  return (
    <Card eyebrow="Uses of Funds" title="Cost Breakdown" testId="financial-chart-cost">
      <div className="grid sm:grid-cols-2 gap-4 items-center">
        <div className="h-60 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="92%" paddingAngle={2} stroke="none">
                {data.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">Total</span>
            <span className="font-mono-num font-semibold text-[#0F2E23]">{usdCompact(total)}</span>
          </div>
        </div>
        <ul className="space-y-2 text-sm">
          {data.map((d, i) => (
            <li key={d.name} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />{d.name}</span>
              <span className="font-mono-num text-xs text-slate-600">{usd(d.value)} · {((d.value / total) * 100).toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
